import {
  AI_AUTOPILOT_USER_ID,
  CHATBOT_USER_ID,
} from "@redotech/ai-autopilot-constants/constant";
import type { ConversationId } from "@redotech/effect-api-shared/ids/definitions";
import {
  MongoUserId,
  type MongoUserId as MongoUserIdType,
} from "@redotech/effect-api-shared/ids/mongo-ids";
import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { useSearch } from "@redotech/react-util/search";
import AiStarIcon from "@redotech/redo-icons/ai-star.svg";
import { useHasPermission } from "@redotech/redo-merchant-app-common/auth/permissions-context";
import { getUserAvatarUrl } from "@redotech/redo-merchant-app-common/get-avatar-url";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { SupportPermissions } from "@redotech/redo-model/permissions";
import { GetUser as User } from "@redotech/redo-model/user";
import type { RedoBadgeProps } from "@redotech/redo-web/arbiter-components/badge/redo-badge";
import type {
  RedoListItem,
  TextRedoListItem,
} from "@redotech/redo-web/arbiter-components/list/redo-list";
import ChatBotIcon from "@redotech/redo-web/icon-old/chat-bot.svg";
import { ObjectId } from "bson";
import Fuse from "fuse.js";
import { useMemo, useState } from "react";
import { useUpdateConversationAssignee } from "./use-conversation-mutations";

const searcher = new Fuse<User>([], {
  keys: ["name", "email"],
  threshold: 0.3,
});

function getUserDisplayName(user: User): string {
  if (user._id === CHATBOT_USER_ID) {
    return "Chatbot";
  }
  if (user._id === AI_AUTOPILOT_USER_ID) {
    return "AI Autopilot";
  }
  return user.name;
}

function getUserLeadingItem(
  user: User,
  displayName: string,
): TextRedoListItem<User>["leadingItem"] {
  if (user._id === CHATBOT_USER_ID) {
    return { type: "icon", Icon: ChatBotIcon };
  }
  if (user._id === AI_AUTOPILOT_USER_ID) {
    return { type: "icon", Icon: AiStarIcon };
  }
  return {
    type: "avatar",
    alt: "User profile picture",
    imageUrl: getUserAvatarUrl({ email: user.email }),
    name: displayName,
  };
}

export interface UseAssigneeSelectorResult {
  // Data
  assigneeListItems: RedoListItem<User | null>[];
  selectedItem: RedoListItem<User | null> | undefined;
  canEditAssignee: boolean;

  // Badge display
  badgeText: string;
  badgeAvatar: RedoBadgeProps["avatar"];

  // Dropdown state
  dropdownOpen: boolean;
  badgeRef: HTMLElement | null;
  setBadgeRef: (ref: HTMLElement | null) => void;

  // Handlers
  handleBadgeClick: () => void;
  handleDropdownOpenChange: (open: boolean) => void;
  handleItemSelected: (user: User | null) => void;

  // Search
  searchText: string;
  setSearchText: (text: string) => void;
}

export function useAssigneeSelector({
  conversationId,
  mongoAssigneeId,
}: {
  conversationId: ConversationId;
  mongoAssigneeId: MongoUserIdType | null;
}): UseAssigneeSelectorResult {
  const team = useRequiredContext(TeamContext);
  const updateAssignee = useUpdateConversationAssignee();
  const [searchText, setSearchText] = useState("");
  const canEditAssignee = useHasPermission(SupportPermissions.EDIT_ASSIGNEE);
  const allUsers = useMemo(
    () => team.users.map((user) => user.user as User),
    [team.users],
  );
  const filteredUsers = useSearch(searcher, allUsers, searchText);
  const assigneeListItems: RedoListItem<User | null>[] = useMemo(() => {
    const userItems: RedoListItem<User>[] = filteredUsers.map((user) => {
      const name = getUserDisplayName(user);
      return {
        type: "text",
        id: MongoUserId.fromObjectId(new ObjectId(user._id)),
        text: name,
        value: user,
        leadingItem: getUserLeadingItem(user, name),
      };
    });

    const unassigned: RedoListItem<null> = {
      type: "text",
      id: "unassigned",
      text: "Unassigned",
      value: null,
    };

    return [unassigned, ...userItems];
  }, [filteredUsers]);

  const currentAssignee = useMemo(() => {
    if (!mongoAssigneeId) {
      return null;
    }
    const hexId = MongoUserId.toObjectId(mongoAssigneeId).toHexString();
    return allUsers.find((user) => user._id === hexId) ?? null;
  }, [mongoAssigneeId, allUsers]);

  const selectedItem: RedoListItem<User | null> | undefined = useMemo(() => {
    if (!currentAssignee) {
      return {
        type: "text",
        id: "unassigned",
        text: "Unassigned",
        value: null,
      };
    }
    const name = getUserDisplayName(currentAssignee);
    return {
      type: "text",
      id: mongoAssigneeId!,
      text: name,
      value: currentAssignee,
      leadingItem: getUserLeadingItem(currentAssignee, name),
    };
  }, [currentAssignee, mongoAssigneeId]);

  // UI state for dropdown
  const [badgeRef, setBadgeRef] = useState<HTMLElement | null>(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const handleBadgeClick = useHandler(() => {
    if (canEditAssignee) {
      setDropdownOpen((open) => !open);
    }
  });

  const handleDropdownOpenChange = useHandler((open: boolean) => {
    setDropdownOpen(open);
    if (!open) {
      setSearchText("");
    }
  });

  const handleItemSelected = useHandler((user: User | null) => {
    const newAssigneeId = user
      ? MongoUserId.fromObjectId(new ObjectId(user._id))
      : null;
    updateAssignee(conversationId, newAssigneeId);
    setDropdownOpen(false);
  });

  // Extract avatar info for badge display
  const badgeAvatar: RedoBadgeProps["avatar"] =
    selectedItem?.leadingItem?.type === "avatar"
      ? {
          alt: selectedItem.leadingItem.alt,
          name: selectedItem.leadingItem.name,
          imageUrl: selectedItem.leadingItem.imageUrl ?? null,
        }
      : undefined;

  return {
    // Data
    assigneeListItems,
    selectedItem,
    canEditAssignee,

    // Badge display
    badgeText: selectedItem?.text || "Unassigned",
    badgeAvatar,

    // Dropdown state
    dropdownOpen,
    badgeRef,
    setBadgeRef,

    // Handlers
    handleBadgeClick,
    handleDropdownOpenChange,
    handleItemSelected,

    // Search
    searchText,
    setSearchText,
  };
}
